import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useToast } from './ToastNotification';
import { InlineLoader } from './LoadingIndicator';
import { OWNED_WORKSPACE_CAP, isAtWorkspaceCap } from '../utils/workspaceCap';

const MAX_NAME_LENGTH = 100;
const CAP_MESSAGE = `Limit of ${OWNED_WORKSPACE_CAP} Workspaces has been reached.`;

/**
 * Create-workspace dialog (#753). Controlled by the parent via open/onClose so
 * it can be mounted from multiple entry points (Account, GroupSwitcher).
 *
 * Gates on the live owned-workspace count: at the cap the name field + Create
 * are disabled and the cap message is shown. The backend is the hard gate - a
 * 403 workspace_limit_reached (e.g. from a stale client count) surfaces the
 * same message.
 */
export const CreateWorkspaceDialog = ({ open, onClose, onCreated }) => {
  const { groups, createWorkspace } = useGroup();
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const atCap = isAtWorkspaceCap(groups);
  const trimmed = name.trim();
  const nameTooLong = trimmed.length > MAX_NAME_LENGTH;
  const canSubmit = !submitting && !atCap && trimmed.length > 0 && !nameTooLong;

  const close = () => {
    if (submitting) return;
    setName('');
    onClose?.();
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    // Disable immediately (the race guard): a second submit cannot fire while
    // the first is in flight.
    setSubmitting(true);
    try {
      const created = await createWorkspace(trimmed);
      showSuccess(`Workspace "${created.name}" created`);
      onCreated?.(created);
      setName('');
      onClose?.();
    } catch (err) {
      const code = err?.body?.code;
      if (err?.status === 403 || code === 'workspace_limit_reached') {
        showError(CAP_MESSAGE);
      } else if (err?.status === 409) {
        showError('A workspace with this name already exists. Pick a different name.');
      } else {
        showError(err?.body?.error || err?.message || 'Failed to create workspace');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Create workspace</DialogTitle>
      <DialogContent>
        {atCap ? (
          <DialogContentText>
            {CAP_MESSAGE} Delete or transfer a workspace to create a new one.
          </DialogContentText>
        ) : (
          <TextField
            autoFocus
            margin="dense"
            label="Workspace name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleCreate(); }}
            error={nameTooLong}
            helperText={nameTooLong
              ? `Name must be ${MAX_NAME_LENGTH} characters or fewer`
              : 'Give your new workspace a name.'}
            disabled={submitting}
            inputProps={{ maxLength: MAX_NAME_LENGTH + 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canSubmit}>
          {submitting ? <InlineLoader message="Creating..." size={16} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateWorkspaceDialog;
